import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import axios from "axios";
import { useBlocker } from "react-router";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { refreshAccessTokenFn } from "src/_Globals/queryClient/refreshToken";
import { invalidateRecalculatedScores } from "src/api/client-api/post/useRecalculatePost";

export type ScoreRecalculationEntity = {
    id: number;
    name: string;
};

export type ScoreRecalculationTarget = {
    entityId: number;
    posts: { postId: number; isLocked: boolean }[];
};

type PendingPost = { postId: number; entityId: number };

export const RECALCULATION_BATCH_SIZE = 20;
export const MAX_RECALCULATION_PAGE_ENTITIES = 100;
const RECALCULATION_REQUEST_TIMEOUT_MS = 300_000;
const TARGETS_REQUEST_TIMEOUT_MS = 60_000;
const POST_UNRELATED_FAILURE_STATUSES = [401, 403, 502, 503, 504];

export type ScoreRecalculationState =
    | { phase: "loading" }
    | { phase: "load-failed" }
    | { phase: "confirm" }
    | { phase: "running" }
    | { phase: "failed"; failedEntityName: string | null }
    | { phase: "done" };

type UseScoreRecalculationOptions = {
    path: string;
    pageEntities: ScoreRecalculationEntity[];
    campaignId: number;
    organizationId: number;
    invalidatedQueryKey: string;
};

const withTokenRefresh = async <T>(request: () => Promise<T>): Promise<T> => {
    try {
        return await request();
    } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 401) throw error;
        await refreshAccessTokenFn();

        return request();
    }
};

const isFailureOfThePost = (error: unknown) =>
    axios.isAxiosError(error) &&
    error.response !== undefined &&
    !POST_UNRELATED_FAILURE_STATUSES.includes(error.response.status);

const takeChunkOfOneEntity = (pendingPosts: PendingPost[], start: number) => {
    const { entityId } = pendingPosts[start];
    const end = pendingPosts.findIndex(
        (post, index) => index > start && (post.entityId !== entityId || index - start === RECALCULATION_BATCH_SIZE),
    );

    return pendingPosts.slice(start, end === -1 ? undefined : end);
};

const recalculatePosts = (postIds: number[], signal: AbortSignal) =>
    withTokenRefresh(() =>
        loggedInApiCall.post(
            "/api/ssq-posts/recalculate",
            { ids: postIds.join(",") },
            { headers: { Accept: "application/json" }, signal, timeout: RECALCULATION_REQUEST_TIMEOUT_MS },
        ),
    );

export const useScoreRecalculation = (options: UseScoreRecalculationOptions) => {
    const { path, pageEntities, campaignId, organizationId, invalidatedQueryKey } = options;
    const queryClient = useQueryClient();
    const [state, setState] = useState<ScoreRecalculationState>({ phase: "loading" });
    const [targets, setTargets] = useState<ScoreRecalculationTarget[]>([]);
    const [doneCount, setDoneCount] = useState(0);
    const abortController = useRef<AbortController | null>(null);

    const pendingPosts = useMemo(
        () =>
            targets.flatMap(({ entityId, posts }) =>
                posts.filter((post) => !post.isLocked).map(({ postId }) => ({ postId, entityId })),
            ),
        [targets],
    );
    const postCount = targets.reduce((count, target) => count + target.posts.length, 0);
    const entityNames = useMemo(() => new Map(pageEntities.map((entity) => [entity.id, entity.name])), [pageEntities]);

    const startRequestScope = useCallback(() => {
        abortController.current?.abort();
        const controller = new AbortController();
        abortController.current = controller;

        return controller;
    }, []);

    const loadTargets = useCallback(async () => {
        const controller = startRequestScope();
        setState({ phase: "loading" });
        setDoneCount(0);
        try {
            const response = await withTokenRefresh(() =>
                loggedInApiCall.get<ScoreRecalculationTarget[]>(`${path}/score-recalculation-targets`, {
                    params: {
                        ids: pageEntities.map((entity) => entity.id),
                        campaign: campaignId,
                        organization_id: organizationId,
                    },
                    headers: { Accept: "application/json" },
                    signal: controller.signal,
                    timeout: TARGETS_REQUEST_TIMEOUT_MS,
                }),
            );
            setTargets(response.data);
            setState({ phase: "confirm" });
        } catch (error) {
            if (axios.isCancel(error)) return;
            setState({ phase: "load-failed" });
        }
    }, [path, pageEntities, campaignId, organizationId, startRequestScope]);

    const runFrom = async (startIndex: number) => {
        const controller = startRequestScope();
        setState({ phase: "running" });

        let index = startIndex;
        try {
            while (index < pendingPosts.length) {
                const chunk = takeChunkOfOneEntity(pendingPosts, index);
                try {
                    await recalculatePosts(
                        chunk.map((post) => post.postId),
                        controller.signal,
                    );
                } catch (error) {
                    if (!isFailureOfThePost(error)) throw error;
                    setState({ phase: "failed", failedEntityName: entityNames.get(chunk[0].entityId) ?? null });
                    return;
                }
                index += chunk.length;
                setDoneCount(index);
            }
            setState({ phase: "done" });
        } catch (error) {
            if (axios.isCancel(error)) return;
            setState({ phase: "failed", failedEntityName: null });
        } finally {
            setDoneCount(index);
            queryClient.invalidateQueries([invalidatedQueryKey]);
            invalidateRecalculatedScores(queryClient);
            if (abortController.current === controller) abortController.current = null;
        }
    };

    useEffect(() => () => abortController.current?.abort(), []);

    const navigationBlocker = useBlocker(state.phase === "running");
    useEffect(() => {
        if (navigationBlocker.state === "blocked") navigationBlocker.reset();
    }, [navigationBlocker]);

    useEffect(() => {
        if (state.phase !== "running") return;

        const warnBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warnBeforeLeaving);

        return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
    }, [state.phase]);

    return {
        state,
        loadTargets,
        start: () => runFrom(0),
        resume: () => runFrom(doneCount),
        questionnaireCount: pendingPosts.length,
        entityCount: new Set(pendingPosts.map((post) => post.entityId)).size,
        lockedCount: postCount - pendingPosts.length,
        doneCount,
    };
};
